import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  GeneratorCategory,
  type PlantUnit,
  type PlantUnitAffiliation,
  PowerType,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { ListPlantUnitsResponse } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'

import { AREA_CODE_OPTIONS } from '~/models/area'
import {
  type CivilDate,
  compareCivilDate,
  protoDateToCivilDate,
  requireCivilDate,
} from '~/models/civilDate'
import type { SortOrder } from '~/models/tableSort'

/** 電源ユニットの所属 (系統コードの BG 所属) 1 件。期間は発電所行 ∩ 計画マスタ ∩ BG 行。 */
export type PlantUnitAffiliationRow = {
  bgMasterId: string
  bgCode: string
  bgName: string
  validFrom: CivilDate
  validTo: CivilDate | undefined
  /** 自社の発電 BG (発電で、自社の構成員がいる。期間は見ない) か。 */
  isOwnGenerationBg: boolean
}

/** 電源ユニット一覧・詳細の表示行。未入力の任意項目は空文字に揃える。 */
export type PlantUnitRow = {
  id: string
  generationSiteId: string
  /** 系統コード (広域機関の発電所) の id。未設定は undefined。 */
  plantMasterId: string | undefined
  unitName: string
  unitNameAbbr: string
  generatorCategory: GeneratorCategory
  powerType: PowerType
  /** 認可出力 (kW)。int64 なので number に直さず bigint のまま持つ (2^53 を超える値を丸めない)。 */
  authorizedOutputKw: bigint
  receivingPointId: string
  powerSourceId: string
  generatorIdentifier: string
  operationStartDate: CivilDate
  operationEndDate: CivilDate | undefined
  validFrom: CivilDate
  validTo: CivilDate | undefined
  note: string
  /** 系統コードのエリア、無ければ発電所のエリア (backend が導出する)。 */
  areaCode: AreaCode
  /** 発電所と系統コードのエリアが違う (エリア不一致。backend が判定する。系統コード未設定は false)。 */
  siteAreaMismatch: boolean
  affiliations: PlantUnitAffiliationRow[]
  createdBy: string | undefined
  createdAt: Timestamp | undefined
  updatedBy: string | undefined
  updatedAt: Timestamp | undefined
}

function convertAffiliation(a: PlantUnitAffiliation): PlantUnitAffiliationRow {
  return {
    bgMasterId: a.bgMasterId,
    bgCode: a.bgCode,
    bgName: a.bgName,
    validFrom: requireCivilDate(
      protoDateToCivilDate(a.validFrom),
      `PlantUnitAffiliation.validFrom (bg=${a.bgCode})`,
    ),
    validTo: protoDateToCivilDate(a.validTo),
    isOwnGenerationBg: a.isOwnGenerationBg,
  }
}

export function convertPlantUnit(unit: PlantUnit): PlantUnitRow {
  return {
    id: unit.id,
    generationSiteId: unit.generationSiteId,
    plantMasterId: unit.plantMasterId,
    unitName: unit.unitName,
    unitNameAbbr: unit.unitNameAbbr,
    generatorCategory: unit.generatorCategory,
    powerType: unit.powerType,
    authorizedOutputKw: unit.authorizedOutputKw,
    receivingPointId: unit.receivingPointId ?? '',
    powerSourceId: unit.powerSourceId ?? '',
    generatorIdentifier: unit.generatorIdentifier ?? '',
    operationStartDate: requireCivilDate(
      protoDateToCivilDate(unit.operationStartDate),
      `PlantUnit.operationStartDate (id=${unit.id})`,
    ),
    operationEndDate: protoDateToCivilDate(unit.operationEndDate),
    validFrom: requireCivilDate(
      protoDateToCivilDate(unit.validFrom),
      `PlantUnit.validFrom (id=${unit.id})`,
    ),
    validTo: protoDateToCivilDate(unit.validTo),
    note: unit.note ?? '',
    areaCode: unit.areaCode,
    siteAreaMismatch: unit.siteAreaMismatch,
    affiliations: unit.affiliations.map(convertAffiliation),
    createdBy: unit.createdBy,
    createdAt: unit.createdAt,
    updatedBy: unit.updatedBy,
    updatedAt: unit.updatedAt,
  }
}

export function convertListPlantUnitsResponse(
  response: ListPlantUnitsResponse,
): PlantUnitRow[] {
  return response.items.map(convertPlantUnit)
}

/** 稼働終了日が今日より前 (廃止済み) か。 */
export function isRetiredOn(unit: PlantUnitRow, today: CivilDate): boolean {
  return (
    unit.operationEndDate !== undefined &&
    compareCivilDate(unit.operationEndDate, today) < 0
  )
}

/** 一覧のフィルタ状態。 */
export type UnitFilter = {
  /** UNSPECIFIED = すべて。 */
  areaCode: AreaCode
  /** UNSPECIFIED = すべて。 */
  category: GeneratorCategory
  /** 廃止済み (稼働終了日が今日より前) も出すか。 */
  includeRetired: boolean
  /** マスタ紐付けが未完了 (系統コード未設定・所属が 1 件も無い・発電所と系統コードのエリア不一致) のユニットだけ出すか。 */
  onlyMissingMaster: boolean
  searchText: string
}

export const DEFAULT_UNIT_FILTER: UnitFilter = {
  areaCode: AreaCode.UNSPECIFIED,
  category: GeneratorCategory.UNSPECIFIED,
  includeRetired: false,
  onlyMissingMaster: false,
  searchText: '',
}

/** 一覧が引くマスタ名 (発電所名・発電所のエリア・系統コード)。 */
export type UnitListMasters = {
  sites: ReadonlyMap<string, { siteName: string; areaCode: AreaCode }>
  /** plant_master id → 系統コード。 */
  plantCodes: ReadonlyMap<string, string>
}

/** 一覧上部の注意に出す、エリア不一致のユニット数 (判定は backend の応答値 `siteAreaMismatch`)。 */
export function countAreaMismatches(units: readonly PlantUnitRow[]): number {
  return units.filter((unit) => unit.siteAreaMismatch).length
}

/**
 * マスタ未設定の状態。
 *
 * - plantCode: 系統コードが未設定
 * - bg: 系統コードはあるが、所属 (期間外を含む) が 1 件も無い
 *
 * 系統コードが無い時は所属を系統コードから引けないので bg は立てない (同じ 1 件を 2 回数えない)。
 */
export type MasterGaps = { plantCode: boolean; bg: boolean }

export function masterGapsOf(unit: PlantUnitRow): MasterGaps {
  if (unit.plantMasterId === undefined) return { plantCode: true, bg: false }
  return { plantCode: false, bg: unit.affiliations.length === 0 }
}

export function hasMasterGap(unit: PlantUnitRow): boolean {
  const gaps = masterGapsOf(unit)
  return gaps.plantCode || gaps.bg
}

/** 一覧上部の注意に出す件数。 */
export function countMasterGaps(units: readonly PlantUnitRow[]): {
  plantCode: number
  bg: number
} {
  let plantCode = 0
  let bg = 0
  for (const unit of units) {
    const gaps = masterGapsOf(unit)
    if (gaps.plantCode) plantCode += 1
    if (gaps.bg) bg += 1
  }
  return { plantCode, bg }
}

/** 検索語がどれかの項目に部分一致するか (空は素通し。前後の空白と大文字小文字は無視)。 */
function matchesKeyword(needle: string, haystacks: readonly string[]): boolean {
  const trimmed = needle.trim().toLowerCase()
  if (trimmed === '') return true
  return haystacks.some((value) => value.toLowerCase().includes(trimmed))
}

/**
 * 一覧のフィルタ判定。エリアは応答の areaCode (系統コードのエリア、無ければ発電所のエリア)、
 * 検索は発電所名・ユニット名・略称・系統コード。
 */
export function matchesUnitFilter(
  unit: PlantUnitRow,
  filter: UnitFilter,
  today: CivilDate,
  masters: UnitListMasters,
): boolean {
  if (
    filter.areaCode !== AreaCode.UNSPECIFIED &&
    unit.areaCode !== filter.areaCode
  ) {
    return false
  }
  if (
    filter.category !== GeneratorCategory.UNSPECIFIED &&
    unit.generatorCategory !== filter.category
  ) {
    return false
  }
  if (!filter.includeRetired && isRetiredOn(unit, today)) return false
  if (
    filter.onlyMissingMaster &&
    !hasMasterGap(unit) &&
    !unit.siteAreaMismatch
  ) {
    return false
  }
  return matchesKeyword(filter.searchText, [
    masters.sites.get(unit.generationSiteId)?.siteName ?? '',
    unit.unitName,
    unit.unitNameAbbr,
    unit.plantMasterId === undefined
      ? ''
      : (masters.plantCodes.get(unit.plantMasterId) ?? ''),
  ])
}

/** エリアの並び順 (北→南、沖縄最後)。一覧にない値は最後。 */
export function areaOrder(areaCode: AreaCode): number {
  const index = AREA_CODE_OPTIONS.indexOf(areaCode)
  return index === -1 ? AREA_CODE_OPTIONS.length : index
}

/** 数字の並びを数として比べる名称の比較 (「2号」<「10号」)。 */
const NAME_COLLATOR = new Intl.Collator('ja', { numeric: true })

export function naturalCompare(a: string, b: string): number {
  return NAME_COLLATOR.compare(a, b)
}

/**
 * 発電所 (サイト) 行の 1 列 = 配下ユニットの値の集約。
 *
 * 配下が 1 種類ならその値 (`value`)、混在なら `value` は undefined (画面は空にし、title に
 * `values` の一覧を出す)。`values` は重複を畳んで昇順。
 */
export type MixedValue<T extends string | number> = {
  value: T | undefined
  values: T[]
}

export function summarizeMixed<T extends string | number>(
  values: readonly T[],
): MixedValue<T> {
  const distinct = [...new Set(values)].sort((a, b) =>
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : naturalCompare(String(a), String(b)),
  )
  return {
    value: distinct.length === 1 ? distinct[0] : undefined,
    values: distinct,
  }
}

/** 一覧の並べ替えの列。発電所 (親) もユニット (子) も同じ列で並べる。 */
export type UnitSortKey = 'name' | 'areaCode' | 'authorizedOutputKw'
export type UnitSort = { key: UnitSortKey; order: SortOrder }

/** 列見出しを押した時の並び: 別の列は昇順から、同じ列は昇順 ↔ 降順。 */
export function nextUnitSort(
  prev: UnitSort | undefined,
  key: UnitSortKey,
): UnitSort {
  return {
    key,
    order: prev?.key === key && prev.order === 'asc' ? 'desc' : 'asc',
  }
}

/** 一覧の発電所 (サイト) 1 つ分 (見出し行の中身)。 */
export type PlantUnitGroup = {
  siteId: string
  siteName: string
  /** 発電所のエリア (並び順に使う)。 */
  siteAreaCode: AreaCode
  /** 絞り込み後の配下ユニット (ユニット名の自然順)。 */
  units: PlantUnitRow[]
  /** 配下ユニットの認可出力の合計 (kW)。 */
  totalAuthorizedOutputKw: bigint
  /** 配下ユニットのエリア (1 種類なら値・混在なら空)。 */
  areaCodes: MixedValue<AreaCode>
  /** 配下で最も早い稼働開始日。 */
  earliestOperationStart: CivilDate | undefined
  /** 配下がすべて廃止済み (稼働終了日あり) のときの、最も遅い稼働終了日。 */
  latestOperationEndWhenAllRetired: CivilDate | undefined
}

function minDate(dates: readonly CivilDate[]): CivilDate | undefined {
  return dates.length === 0
    ? undefined
    : dates.reduce((a, b) => (compareCivilDate(a, b) <= 0 ? a : b))
}

function maxDate(dates: readonly CivilDate[]): CivilDate | undefined {
  return dates.length === 0
    ? undefined
    : dates.reduce((a, b) => (compareCivilDate(a, b) >= 0 ? a : b))
}

/**
 * 絞り込み済みのユニットを発電所 (サイト) ごとに束ねて並べる。
 *
 * 発電所はエリア順 (北→南) → 名称の自然順。配下 0 件の発電所は出ない。
 * 発電所の中はユニット名の自然順。発電所が引けないユニット (マスタに無いサイト) も
 * 落とさず、サイトごとの別グループで出す。
 */
export function groupUnitsBySite(
  units: readonly PlantUnitRow[],
  masters: UnitListMasters,
  sort?: UnitSort,
): PlantUnitGroup[] {
  const bySiteId = new Map<string, PlantUnitRow[]>()
  for (const unit of units) {
    const list = bySiteId.get(unit.generationSiteId) ?? []
    list.push(unit)
    bySiteId.set(unit.generationSiteId, list)
  }
  const groups: PlantUnitGroup[] = []
  for (const [siteId, members] of bySiteId) {
    const ordered = [...members].sort(
      (a, b) =>
        naturalCompare(a.unitName, b.unitName) || (a.id < b.id ? -1 : 1),
    )
    const site = masters.sites.get(siteId)
    const endDates = ordered
      .map((unit) => unit.operationEndDate)
      .filter((date): date is CivilDate => date !== undefined)
    groups.push({
      siteId,
      siteName: site?.siteName ?? '',
      siteAreaCode: site?.areaCode ?? AreaCode.UNSPECIFIED,
      units: ordered,
      totalAuthorizedOutputKw: ordered.reduce(
        (sum, unit) => sum + unit.authorizedOutputKw,
        BigInt(0),
      ),
      areaCodes: summarizeMixed(ordered.map((unit) => unit.areaCode)),
      earliestOperationStart: minDate(
        ordered.map((unit) => unit.operationStartDate),
      ),
      latestOperationEndWhenAllRetired:
        endDates.length === ordered.length ? maxDate(endDates) : undefined,
    })
  }
  const byDefault = (a: PlantUnitGroup, b: PlantUnitGroup): number =>
    areaOrder(a.siteAreaCode) - areaOrder(b.siteAreaCode) ||
    naturalCompare(a.siteName, b.siteName) ||
    (a.siteId < b.siteId ? -1 : a.siteId > b.siteId ? 1 : 0)
  if (sort === undefined) return groups.sort(byDefault)
  const direction = sort.order === 'asc' ? 1 : -1
  for (const group of groups) {
    group.units.sort(
      (a, b) =>
        direction * compareUnitsBy(sort.key, a, b) ||
        naturalCompare(a.unitName, b.unitName) ||
        (a.id < b.id ? -1 : 1),
    )
  }
  return groups.sort((a, b) => {
    // 空欄のエリアと未所属は、並べ替えの方向によらず末尾に置く。
    if (a.siteId === '' || b.siteId === '') {
      if (a.siteId !== b.siteId) return a.siteId === '' ? 1 : -1
    }
    if (sort.key === 'areaCode') {
      const aBlank = groupAreaOrder(a) === AREA_CODE_OPTIONS.length
      const bBlank = groupAreaOrder(b) === AREA_CODE_OPTIONS.length
      if (aBlank !== bBlank) return aBlank ? 1 : -1
    }
    return direction * compareGroupsBy(sort.key, a, b) || byDefault(a, b)
  })
}

/** bigint の比較 (差を number にすると 2^53 を超える差が丸まるので、大小だけ返す)。 */
function compareBigint(a: bigint, b: bigint): number {
  if (a < b) return -1
  return a > b ? 1 : 0
}

/** ユニット同士を列で比べる (昇順の向き)。 */
function compareUnitsBy(
  key: UnitSortKey,
  a: PlantUnitRow,
  b: PlantUnitRow,
): number {
  switch (key) {
    case 'name':
      return naturalCompare(a.unitName, b.unitName)
    case 'areaCode':
      return areaOrder(a.areaCode) - areaOrder(b.areaCode)
    case 'authorizedOutputKw':
      return compareBigint(a.authorizedOutputKw, b.authorizedOutputKw)
  }
}

/** 発電所 (見出し行) 同士を同じ列で比べる: 名称 / 単一のエリア / 認可出力の合計。 */
function compareGroupsBy(
  key: UnitSortKey,
  a: PlantUnitGroup,
  b: PlantUnitGroup,
): number {
  switch (key) {
    case 'name':
      return naturalCompare(a.siteName, b.siteName)
    case 'areaCode':
      return groupAreaOrder(a) - groupAreaOrder(b)
    case 'authorizedOutputKw':
      return compareBigint(a.totalAuthorizedOutputKw, b.totalAuthorizedOutputKw)
  }
}

function groupAreaOrder(group: PlantUnitGroup): number {
  return group.areaCodes.value === undefined
    ? AREA_CODE_OPTIONS.length
    : areaOrder(group.areaCodes.value)
}

/** 一覧の 1 行。`plant` は発電所 (サイト) の見出し行、`unit` は配下のユニット行。 */
export type GroupedUnitRow =
  | {
      kind: 'plant'
      key: string
      group: PlantUnitGroup
      /** 配下のユニットを折りたたんでいる。 */
      collapsed: boolean
    }
  | { kind: 'unit'; key: string; group: PlantUnitGroup; unit: PlantUnitRow }

/** 見出し行 + ユニット行を並べる。`collapsedSiteIds` の発電所は見出し行だけ。 */
export function flattenGroups(
  groups: readonly PlantUnitGroup[],
  collapsedSiteIds: ReadonlySet<string>,
): GroupedUnitRow[] {
  const rows: GroupedUnitRow[] = []
  for (const group of groups) {
    if (group.units.length === 0) continue
    const collapsed = collapsedSiteIds.has(group.siteId)
    rows.push({ kind: 'plant', key: `site:${group.siteId}`, group, collapsed })
    if (collapsed) continue
    for (const unit of group.units) {
      rows.push({ kind: 'unit', key: unit.id, group, unit })
    }
  }
  return rows
}

/** 電源種別の選択肢 (ユニットでは FIT特例② を選べない)。 */
export const UNIT_POWER_TYPE_OPTIONS: readonly PowerType[] = [
  PowerType.ADJUSTABLE,
  PowerType.NON_ADJUSTABLE,
  PowerType.FIT_EXCEPTION_1,
  PowerType.FIT_EXCEPTION_3,
  PowerType.FIP,
]

/** 発電形式の選択肢 (登録区分 11 値。design の並び: 原子力 / 水力 / 揚水 / 火力 / 地熱 / 風力 / 太陽光・太陽熱 / 蓄電池 / その他)。 */
export const GENERATOR_CATEGORY_OPTIONS: readonly GeneratorCategory[] = [
  GeneratorCategory.NUCLEAR,
  GeneratorCategory.HYDRO,
  GeneratorCategory.PUMPED_STORAGE,
  GeneratorCategory.THERMAL_COAL,
  GeneratorCategory.THERMAL_GAS,
  GeneratorCategory.THERMAL_OIL,
  GeneratorCategory.GEOTHERMAL,
  GeneratorCategory.WIND,
  GeneratorCategory.SOLAR,
  GeneratorCategory.STORAGE,
  GeneratorCategory.OTHER,
]
